import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import AssemblyDetail from './AssemblyDetail';
import { BackflowAssembly } from './types';
import { PortalWithState } from 'react-portal';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import StatusChip from '@/components/Status/StatusChip';
import { assemblyStatusLabel, assemblyStatusStyle } from './assemblyStatus';
import Button from '../Button/Button';
import AssemblyEditModal from './AssemblyEditModal';
import useMode from '../../hooks/useMode';
import { Flex } from 'rebass';
import MoveAssemblyModal from './MoveAssemblyModal';
import AssemblyHistory from './AssemblyHistory';
import { ApolloError } from '@apollo/client/errors';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';
import { gql as company_gql } from '@/modes/company/gql';
import AddAssemblyJobsModal from './AddAssemblyJobsModal';
import RequirePerms from '@/components/RequirePerms';

export const GET_ASSEMBLY = waterSupplier_gql(`#graphql
  query GetAssembly($where: JSON) {
    backflowAssemblies(where: $where) {
      id
      customerId
      customerName
      status
      replacedByAssembly {
        id
        created
        session {
          userProfile {
            id
            name
          }
        }
      }
      modelId
      addressId
      waterServiceId
      waterSupplierId
      model {
        id
        name
        backflowAssemblyMake {
          id
          name
        }
      }
      size
      type
      testStatus
      scheduledCompany
      lastTest {
        jobId
        finalResult
      }
      lastNoticeSent {
        created
        notice {
          id
          type
        }
      }
      serialNumber
      removedByJobId
      location
      testAnniversary
      lastTestDate
      installDate
      hazardType
      hazardLevel
      protectionType
      tags
      address(required: true) {
        street1
        street2
        city
        state
        zip
        zipExt
      }
      waterService {
        id
        type
        status
        siteNumber
        meterNumber
        radioId
        pin
      }
      noticesSent {
        id
        notice {
          type
          profile {
            id
            name
          }
        }
        created
      }
      backflowTests {
        jobId
        job {
          formDatas(where: { field: ["comments", "installation_status"] }) {
            field
            value
          }
          created
        }
        waterSupplierStatus
        dateTested
        testerName
        testerSubmitted
        testingCompanyName
        testingCompanySubmitted
        backflowAssemblyEdited
        initialResult
        finalResult
      }
      versions {
        id
        serialNumber
        type
        size
        location
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
        session {
          userProfile {
            id
            name
          }
        }
        created
      }
      attachments {
        id
        contentType
        url
        thumb(size: 500)
      }
    }
  }
`);

export const GET_ASSEMBLY_COMPANY = company_gql(`#graphql
  query GetAssembly($where: JSON) {
    backflowAssemblies(where: $where) {
      id
      customerId
      customerName
      status
      modelId
      addressId
      waterSupplierId
      waterSupplierProfile {
        id
        name
      }
      model {
        id
        name
        backflowAssemblyMake {
          id
          name
        }
      }
      size
      type
      testStatus
      serialNumber
      location
      testAnniversary
      lastTestDate
      installDate
      hazardType
      hazardLevel
      protectionType
      tags
      address(required: true) {
        street1
        street2
        city
        state
        zip
        zipExt
      }
      waterService {
        id
        pin
        meterNumber
      }
      serviceAddresses {
        id
        customer {
          status
          id
          profile {
            id
            name
          }
        }
      }
      attachments {
        id
        contentType
        url
        thumb(size: 500)
      }
    }
  }
`);

const SET_ASSEMBLY_STATUS = waterSupplier_gql(`#graphql
  mutation SetAssemblyStatus($id: ID!, $status: BackflowAssemblyStatus!) {
    setAssemblyStatus(assemblyId: $id, status: $status) {
      id
      status
    }
  }
`);

type AssemblyThirdPaneProps = {
  assemblyId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onRowClick?: (item: { id: string }) => void;
  refetch?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function AssemblyThirdPane({
  assemblyId,
  onPrev,
  onNext,
  onToggle,
  onRowClick,
  prevEnabled,
  nextEnabled,
  refetch,
}: AssemblyThirdPaneProps): JSX.Element {
  const mode = useMode();

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch: refetchAssembly,
  } = useQuery<{
    backflowAssemblies: BackflowAssembly[];
  }>(mode === 'water-supplier' ? GET_ASSEMBLY : GET_ASSEMBLY_COMPANY, {
    skip: !assemblyId,
    variables: { where: { id: assemblyId || null } },
  });

  const assembly: BackflowAssembly | undefined =
    itemData?.backflowAssemblies?.[0];

  if (itemError) {
    throw itemError;
  }

  const [
    setAssemblyStatus,
    { data, loading: mutationLoading, error: mutationError },
  ] = useMutation(SET_ASSEMBLY_STATUS, {
    onError: useCallback((error: ApolloError) => {
      console.error('Error updating assembly status', error);
    }, []),
  });

  const activateDeactivate = () => {
    if (!assembly) {
      return;
    }
    assembly.status === 'ACTIVE'
      ? setAssemblyStatus({
          variables: { id: assembly.id, status: 'INACTIVE' },
        })
      : setAssemblyStatus({
          variables: { id: assembly.id, status: 'ACTIVE' },
        });
  };

  const canCreateJobs = mode === 'company' || mode === 'water-supplier';

  return (
    <ThirdPane visible={!!assemblyId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={
          assembly ? (
            <>
              {assembly.serialNumber}{' '}
              <StatusChip status={assemblyStatusStyle(assembly)}>
                {assemblyStatusLabel(assembly)}
              </StatusChip>
            </>
          ) : (
            'Loading...'
          )
        }
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        {assembly && canCreateJobs && assembly.status === 'ACTIVE' && (
          <RequirePerms jobs="CREATE">
            <PortalWithState closeOnOutsideClick closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button variant="primary" icon="plus" onClick={openPortal}>
                    Add Backflow Test
                  </Button>
                  {portal(
                    <AddAssemblyJobsModal
                      backflowAssemblyIds={[assemblyId as string]}
                      onClose={closePortal}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        )}
        {mode === 'water-supplier' && !assembly && (
          <Button mr={3} variant="primary" style={{ visibility: 'hidden' }}>
            Hidden
          </Button>
        )}
        {assembly &&
          mode === 'water-supplier' &&
          !assembly.replacedByAssembly && (
            <>
              <RequirePerms jobs="UPDATE">
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button mr={3} variant="outline" onClick={openPortal}>
                        Update
                      </Button>
                      {portal(
                        <AssemblyEditModal
                          action="edit"
                          onClose={(itemId) => {
                            if (itemId && itemId !== assembly?.id) {
                              onRowClick?.({ id: itemId });
                              refetch?.();
                            }
                            closePortal();
                          }}
                          assembly={assembly}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>

                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button mr={3} variant="outline" onClick={openPortal}>
                        Replace
                      </Button>

                      {portal(
                        <AssemblyEditModal
                          action="replace"
                          onClose={(itemId) => {
                            if (itemId && itemId !== assembly?.id) {
                              onRowClick?.({ id: itemId });
                              refetch?.();
                            }
                            closePortal();
                          }}
                          assembly={assembly}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>

                <PortalWithState>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button variant="outline" onClick={openPortal}>
                        Move
                      </Button>
                      {portal(
                        <MoveAssemblyModal
                          id={assemblyId as string}
                          onClose={closePortal}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
                <Button
                  mr={3}
                  onClick={activateDeactivate}
                  variant={assembly.status === 'ACTIVE' ? 'danger' : 'primary'}
                >
                  {assembly.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </Button>
              </RequirePerms>
            </>
          )}
      </ThirdPaneHeader>
      <ThirdPaneContent>
        <Flex height="100%" flexDirection="row" flexGrow={1} flexShrink={1}>
          <Flex
            flexDirection="column"
            flexGrow={1}
            flexShrink={1}
            height="100%"
            width={400}
            overflowY="scroll"
          >
            <AssemblyDetail assembly={assembly} onRefetch={refetchAssembly} />
          </Flex>
          {assembly?.backflowTests &&
            assembly?.noticesSent &&
            assembly?.versions && (
              <Flex
                flexDirection="column"
                flexGrow={1}
                flexShrink={1}
                height="100%"
                width={325}
                sx={{
                  borderLeft: '1px solid',
                  borderLeftColor: 'tableBorder',
                }}
              >
                <AssemblyHistory
                  replacedBy={assembly.replacedByAssembly}
                  backflowTests={assembly.backflowTests}
                  noticesSent={assembly.noticesSent}
                  versions={assembly.versions}
                />
              </Flex>
            )}
        </Flex>
      </ThirdPaneContent>
    </ThirdPane>
  );
}
